import { Suspense, useEffect, useState, useMemo, useCallback } from "react";
import { Canvas } from "@react-three/fiber";
import {
  OrbitControls,
  Preload,
  useGLTF,
  Environment,
  BakeShadows,
  ContactShadows,
} from "@react-three/drei";
import CanvasLoader from "./Loader";
import * as THREE from "three";

const Car = ({ isMobile, isTablet, isLaptop, isDesktop }) => {
  const car = useGLTF("./car.glb", true);

  const updateMaterials = useCallback((scene) => {
    scene.traverse((child) => {
      if (child.isMesh) {
        child.castShadow = true;
        child.receiveShadow = true;

        if (child.material) {
          if (
            child.name === "Object_45" &&
            child.material.name === "GlassRedLights"
          ) {
            const redColor = new THREE.Color("#4b0000").multiplyScalar(4);
            child.material.color = redColor;
            child.material.emissive = redColor;
          }

          const materialProps = {
            envMapIntensity: 2.5,
            roughness: 0.2,
            metalness: 0.9,
            clearcoat: 0.5,
            clearcoatRoughness: 0.1,
            needsUpdate: true,
          };

          Object.assign(child.material, materialProps);

          if (child.material.map) {
            child.material.map.anisotropy = 16;
          }
        }
      }
    });
  }, []);

  useEffect(() => {
    updateMaterials(car.scene);
    return () => {
      car.scene.traverse((child) => {
        if (child.isMesh) {
          child.geometry.dispose();
          if (child.material) {
            child.material.dispose();
          }
        }
      });
    };
  }, [car.scene, updateMaterials]);

  const scale = useMemo(
    () =>
      isMobile ? 1.0 : isTablet ? 0.8 : isLaptop ? 1.3 : isDesktop ? 1.7 : 2.2,
    [isMobile, isTablet, isLaptop, isDesktop]
  );

  const position = useMemo(
    () => (isMobile || isTablet || isLaptop ? [0, -0.5, 0] : [0, -1, 0]),
    [isMobile, isTablet, isLaptop]
  );

  return (
    <group>
      <mesh castShadow receiveShadow>
        <primitive
          object={car.scene}
          scale={scale}
          position={position}
          rotation={[0, -180, 0]}
        />
      </mesh>
      <ContactShadows
        opacity={0.8}
        scale={10}
        blur={2}
        far={4}
        resolution={256}
        color="#000000"
      />
    </group>
  );
};

const CarCanvas = () => {
  const [deviceType, setDeviceType] = useState({
    isMobile: false,
    isTablet: false,
    isLaptop: false,
    isDesktop: false,
  });

  useEffect(() => {
    const queries = {
      mobile: window.matchMedia("(max-width: 500px)"),
      tablet: window.matchMedia("(min-width: 501px) and (max-width: 768px)"),
      laptop: window.matchMedia("(min-width: 769px) and (max-width: 1024px)"),
      desktop: window.matchMedia("(min-width: 1025px) and (max-width: 1400px)"),
    };

    const updateDeviceType = () => {
      setDeviceType({
        isMobile: queries.mobile.matches,
        isTablet: queries.tablet.matches,
        isLaptop: queries.laptop.matches,
        isDesktop: queries.desktop.matches,
      });
    };

    updateDeviceType();

    let timeoutId;
    const debouncedUpdate = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateDeviceType, 250);
    };

    Object.values(queries).forEach((query) => {
      query.addEventListener("change", debouncedUpdate);
    });

    return () => {
      clearTimeout(timeoutId);
      Object.values(queries).forEach((query) => {
        query.removeEventListener("change", debouncedUpdate);
      });
    };
  }, []);

  const { isMobile, isTablet } = deviceType;

  const cameraSettings = useMemo(
    () => ({
      position: [
        isMobile ? 8 : isTablet ? 10 : 12,
        isMobile ? 1 : isTablet ? 1.5 : 2,
        isMobile ? 2 : isTablet ? 2.5 : 3,
      ],
      fov: isMobile ? 30 : isTablet ? 27 : 25,
    }),
    [isMobile, isTablet]
  );

  const glSettings = useMemo(
    () => ({
      preserveDrawingBuffer: true,
      antialias: true,
      alpha: true,
      powerPreference: "high-performance",
      precision: "highp",
      toneMapping: THREE.ACESFilmicToneMapping,
      outputEncoding: "sRGBEncoding",
      shadowMap: {
        enabled: true,
        type: THREE.PCFSoftShadowMap,
      },
    }),
    []
  );

  const orbitSettings = useMemo(
    () => ({
      minPolarAngle: Math.PI / 4,
      maxPolarAngle: Math.PI / 2,
      minDistance: isMobile ? 6 : isTablet ? 7 : 8,
      maxDistance: isMobile ? 15 : isTablet ? 17 : 20,
      enableDamping: true,
      dampingFactor: 0.05,
      enableZoom: false,
    }),
    [isMobile, isTablet]
  );

  const containerClass = useMemo(
    () =>
      `w-full ${
        isMobile ? "h-[50vh]" : "h-[90vh]"
      } flex justify-center items-center`,
    [isMobile]
  );

  const lights = useMemo(
    () => [
      {
        type: "spot",
        position: [0, 15, 0],
        angle: 0.25,
        penumbra: 1,
        castShadow: true,
        intensity: 3,
        shadowBias: -0.0001,
        shadowMapSize: [512, 512],
      },
      {
        type: "ambient",
        intensity: 0.6,
      },
      // You can remove these spot lights if want to better performance
      // {
      //   type: "spot",
      //   position: [-5, 5, 0],
      //   color: "#ffd7c4",
      //   intensity: 2.5,
      //   angle: 0.5,
      //   penumbra: 0.8,
      // },
      // {
      //   type: "spot",
      //   position: [5, 3, 5],
      //   color: "#b8d8ff",
      //   intensity: 1.5,
      //   angle: 0.8,
      //   penumbra: 0.6,
      // },
      // {
      //   type: "spot",
      //   position: [-8, 2, 0],
      //   color: "#ff9f1c",
      //   intensity: 1.2,
      //   angle: 0.3,
      //   penumbra: 0.9,
      //   distance: 12,
      // },
    ],
    []
  );

  return (
    <div className={containerClass}>
      <Canvas
        frameloop="demand"
        shadows
        dpr={[1, 2]}
        camera={cameraSettings}
        gl={glSettings}
        performance={{ min: 0.5 }}
      >
        <color attach="background" args={["#1a1a1a"]} />
        <fog attach="fog" args={["#1a1a1a", 12, 25]} />

        <Suspense fallback={<CanvasLoader />}>
          <Environment preset="sunset" background={false} blur={0.8} />

          {lights.map((light, index) => {
            if (light.type === "ambient") {
              return <ambientLight key={index} intensity={light.intensity} />;
            }
            return (
              <spotLight
                key={index}
                position={light.position}
                color={light.color}
                intensity={light.intensity}
                angle={light.angle}
                penumbra={light.penumbra}
                distance={light.distance}
                castShadow={light.castShadow}
                shadow-bias={light.shadowBias}
                shadow-mapSize={light.shadowMapSize}
              />
            );
          })}

          <OrbitControls {...orbitSettings} />

          <Car {...deviceType} />
          <BakeShadows />
        </Suspense>

        <Preload all />
      </Canvas>
    </div>
  );
};

export default CarCanvas;
